import { useState, useEffect } from 'react';
import VideoModal from './components/VideoModal';

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);

  // Check URL parameters on mount to auto-open video modal
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('demo') === 'true' || urlParams.get('video') === 'true') {
      setIsVideoModalOpen(true);
      // Clean up URL without reloading the page
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  return (
    <div className="landing-page">
      <div id="top"></div>

      {/* Navigation */}
      <header className="sticky top-0 bg-white shadow-sm z-50">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              {/* Mobile menu button */}
              <button
                type="button"
                className="md:hidden inline-flex items-center justify-center p-2 mr-2 rounded-md text-gray-500 hover:text-indigo-600"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <span className="sr-only">Open main menu</span>
                <svg className={`${mobileMenuOpen ? 'hidden' : 'block'} h-6 w-6`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <svg className={`${mobileMenuOpen ? 'block' : 'hidden'} h-6 w-6`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="flex-shrink-0 flex items-center">
                <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }) }} className="text-xl font-bold flex items-center gradient-text">
                  <i className="fas fa-heart mr-2"></i>
                  Apprecio
                </a>
              </div>
              <div className="hidden md:ml-10 md:flex md:space-x-8">
                <a href="#platform" className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-indigo-600">Platform</a>
                <a href="#how-it-works" className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-indigo-600">How It Works</a>
                <a href="#privacy" className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-indigo-600">Privacy</a>
                <a href="#impact" className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-indigo-600">Impact</a>
                <a href="#pricing" className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 hover:text-indigo-600">Pricing</a>
              </div>
            </div>
            <div className="flex items-center">
              <a
                href="https://dashboard.apprecio.app"
                className="ml-3 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Get Started
              </a>
            </div>
          </div>

          {/* Mobile menu */}
          <div className={`${mobileMenuOpen ? 'block' : 'hidden'} md:hidden`}>
            <div className="pt-2 pb-3 space-y-1">
              <a href="#platform" onClick={() => setMobileMenuOpen(false)} className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-500 hover:text-indigo-600 hover:border-indigo-300">Platform</a>
              <a href="#how-it-works" onClick={() => setMobileMenuOpen(false)} className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-500 hover:text-indigo-600 hover:border-indigo-300">How It Works</a>
              <a href="#privacy" onClick={() => setMobileMenuOpen(false)} className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-500 hover:text-indigo-600 hover:border-indigo-300">Privacy</a>
              <a href="#impact" onClick={() => setMobileMenuOpen(false)} className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-500 hover:text-indigo-600 hover:border-indigo-300">Impact</a>
              <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block pl-3 pr-4 py-2 border-l-4 border-transparent text-base font-medium text-gray-500 hover:text-indigo-600 hover:border-indigo-300">Pricing</a>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-indigo-700 via-purple-700 to-indigo-800">
        <div className="max-w-7xl mx-auto pt-16 pb-20 px-4 sm:pt-24 sm:pb-32 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center">
            <div className="w-full md:w-1/2 md:pr-10">
              <span className="inline-block px-3 py-1 text-xs font-semibold text-indigo-900 bg-indigo-200 rounded-full mb-4">
                Culture Building Platform
              </span>
              <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl md:text-6xl">
                <span className="block">Build the culture</span>
                <span className="block text-indigo-200">your team deserves</span>
              </h1>
              <p className="mt-6 text-xl text-indigo-100 max-w-md">
                Transform your workplace by aligning daily actions with company values. Make recognition meaningful, measurable, and part of your DNA.
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <a
                  href="https://dashboard.apprecio.app"
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-indigo-700 bg-white hover:bg-indigo-50"
                >
                  Get Started
                </a>
                <button
                  onClick={() => setIsVideoModalOpen(true)}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-900 bg-opacity-40 hover:bg-opacity-50"
                >
                  <span>Watch Demo</span>
                  <svg className="ml-2 -mr-1 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              <div className="mt-4 flex items-center text-indigo-100 text-sm">
                <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Free for up to 5 users • No credit card required
              </div>
            </div>

            <div className="w-full md:w-1/2 mt-12 md:mt-0">
              <div className="relative mx-auto w-full">
                <div className="mb-3">
                  <img className="rounded-lg shadow-lg w-full" src="/assets/slack-1.png" alt="Apprecio in Slack" />
                </div>

                <div className="relative h-[400px]">
                  <div className="absolute left-0 top-0 w-[85%] z-10">
                    <img className="rounded-lg shadow-xl w-full border border-gray-100" src="/assets/send-appreciation-web.png" alt="Send appreciation form" />
                  </div>

                  <div className="absolute right-0 top-[20%] w-[85%] z-20">
                    <img className="rounded-lg shadow-xl w-full border border-gray-100" src="/assets/sent-appreciation-web.png" alt="Appreciation confirmation" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold sm:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
              Culture Isn't Built By Accident
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              Most companies have great values on their walls. But without a system to reinforce them daily, they remain just words.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-red-500">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <svg className="h-6 w-6 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                The Challenge
              </h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  <span>Company values are forgotten in daily work</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  <span>Recognition is random and inconsistent</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  <span>Great work goes unnoticed and unrewarded</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  <span>No data on cultural health or engagement</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-2">•</span>
                  <span>Employees feel disconnected from company mission</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-8 rounded-xl shadow-lg border-l-4 border-indigo-500">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <svg className="h-6 w-6 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                The Apprecio Way
              </h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">✓</span>
                  <span>Every recognition tied to your values</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">✓</span>
                  <span>Make appreciation a daily habit</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">✓</span>
                  <span>Celebrate contributions in real-time</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">✓</span>
                  <span>Track culture metrics that matter</span>
                </li>
                <li className="flex items-start">
                  <span className="text-indigo-600 mr-2">✓</span>
                  <span>Build meaningful connections across teams</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section id="platform" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold sm:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
              Build Culture Through Recognition
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              Transform workplace recognition into a culture-building system that aligns daily actions with company values.
            </p>
          </div>

          {/* Feature 1: Culture Where Work Happens */}
          <div className="flex flex-wrap items-center mb-20">
            <div className="w-full md:w-1/2 md:pr-10 mb-10 md:mb-0">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Culture Where Work Happens</h3>
              <p className="text-lg text-gray-600 mb-6">
                Bring recognition into your team's daily workflow. Appreciate teammates in Slack or our web platform—no context switching, no disruption, just seamless culture building.
              </p>
              <ul className="space-y-3">
                {['Recognize colleagues without leaving Slack', 'One-click appreciation with heart emoji reactions', 'Public celebrations in team channels build visibility', 'Simple web portal for sending appreciations anywhere', 'Automatic syncing keeps everything in one place'].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <svg className="flex-shrink-0 h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full md:w-1/2">
              <div className="bg-gray-50 p-4 rounded-lg shadow-md">
                <img className="rounded-lg w-full" src="/assets/slack-emoji-appreciation.png" alt="One-click appreciation with emoji reactions" />
              </div>
            </div>
          </div>

          {/* Feature 2: Make Recognition Fun */}
          <div className="flex flex-wrap flex-row-reverse items-center mb-20">
            <div className="w-full md:w-1/2 md:pl-10 mb-10 md:mb-0">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Make Recognition Fun</h3>
              <p className="text-lg text-gray-600 mb-6">
                Gamify culture building with friendly recognition stats and leaderboards. Celebrate your culture champions and motivate everyone to appreciate great work.
              </p>
              <ul className="space-y-3">
                {['Karma points for giving and receiving recognition', 'Friendly leaderboards showcase top recognizers', 'Celebrate your most appreciated teammates', 'Make culture building engaging and fun'].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <svg className="flex-shrink-0 h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full md:w-1/2">
              <div className="bg-gray-50 p-4 rounded-lg shadow-md">
                <img className="rounded-lg w-full" src="/assets/karma.png" alt="Karma points and recognition leaderboards" />
              </div>
            </div>
          </div>

          {/* Feature 3: Living Culture Feed */}
          <div className="flex flex-wrap items-center mb-20">
            <div className="w-full md:w-1/2 md:pr-10 mb-10 md:mb-0">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Make Your Culture Visible Company-Wide</h3>
              <p className="text-lg text-gray-600 mb-6">
                Every recognition appears in a real-time feed that showcases your culture in action. New hires see what's celebrated, teams see cross-functional appreciation, and your values become visible daily.
              </p>
              <ul className="space-y-3">
                {['Real-time feed of all recognitions across the company', 'See who\'s being celebrated and why', 'New employees learn culture through real examples', 'Filter by team, department, or company value'].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <svg className="flex-shrink-0 h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full md:w-1/2">
              <div className="bg-gray-50 p-4 rounded-lg shadow-md">
                <img className="rounded-lg w-full" src="/assets/appreciation-feed-2.png" alt="Living culture feed" />
              </div>
            </div>
          </div>

          {/* Feature 4: Values-Driven Recognition */}
          <div className="flex flex-wrap flex-row-reverse items-center mb-20">
            <div className="w-full md:w-1/2 md:pl-10 mb-10 md:mb-0">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Turn Recognition Into Culture Data</h3>
              <p className="text-lg text-gray-600 mb-6">
                Every appreciation becomes culture intelligence. Tag recognitions with your company values, see which values are being demonstrated daily, and identify the teammates who embody your culture.
              </p>
              <ul className="space-y-3">
                {['Tag every recognition with company values', 'Track which values are most demonstrated', 'See values adoption trends across teams', 'Identify culture champions and values exemplars'].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <svg className="flex-shrink-0 h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full md:w-1/2">
              <div className="bg-gray-50 p-4 rounded-lg shadow-md">
                <img className="rounded-lg w-full" src="/assets/analytics-dashboard.png" alt="Values tracking and culture analytics" />
              </div>
            </div>
          </div>

          {/* Feature 5: Birthday and Anniversary Reminders */}
          <div className="flex flex-wrap items-center mb-20">
            <div className="w-full md:w-1/2 md:pr-10 mb-10 md:mb-0">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Never Miss a Celebration</h3>
              <p className="text-lg text-gray-600 mb-6">
                Strengthen relationships by celebrating important milestones. Automated reminders ensure your team never misses a birthday or work anniversary, making every team member feel valued.
              </p>
              <ul className="space-y-3">
                {['Automatic birthday and work anniversary reminders', 'Celebrate milestones directly in Slack', 'Encourage team-wide appreciation on special days', 'Build stronger connections through personal recognition'].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <svg className="flex-shrink-0 h-6 w-6 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-600">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full md:w-1/2">
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-8 rounded-lg shadow-md flex items-center justify-center min-h-[300px]">
                <div className="text-center">
                  <div className="flex items-center justify-center mb-4">
                    <svg className="h-20 w-20 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                    </svg>
                  </div>
                  <p className="text-2xl font-bold text-indigo-900 mb-2">Celebrate Together</p>
                  <p className="text-gray-600">Automated birthday & anniversary reminders</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold sm:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
              How It Works
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              Get started in minutes. No complex setup, no training required.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {/* Step 1 */}
            <div className="text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 rounded-full bg-indigo-100 text-indigo-600">
                <span className="text-2xl font-bold">1</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Create Your Account</h3>
              <p className="text-gray-600">
                Sign up and define your company values. Invite your team members to join in.
              </p>
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 rounded-full bg-indigo-100 text-indigo-600">
                <span className="text-2xl font-bold">2</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Connect Slack App</h3>
              <p className="text-gray-600">
                Connect Apprecio to your Slack workspace. No technical setup required.
              </p>
            </div>

            {/* Step 3 */}
            <div className="text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-6 rounded-full bg-indigo-100 text-indigo-600">
                <span className="text-2xl font-bold">3</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Start Building Culture</h3>
              <p className="text-gray-600">
                Send your appreciations. Track values alignment and celebrate your team.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Privacy Section */}
      <section id="privacy" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold sm:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">Your Privacy Matters</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              We take data privacy seriously. Here's how we protect your information.
            </p>
          </div>

          <div className="mt-16 grid gap-8 md:grid-cols-3">
            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex items-center justify-center w-12 h-12 mb-5 rounded-md bg-indigo-100 text-indigo-600">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Limited Data Access</h3>
              <p className="text-gray-600">
                Apprecio bot in Slack is only activated when tagged, never scanning conversations or private messages.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex items-center justify-center w-12 h-12 mb-5 rounded-md bg-indigo-100 text-indigo-600">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">Enterprise-Grade Security</h3>
              <p className="text-gray-600">
                All data is encrypted in transit and at rest. Your data is never sold or shared with third parties.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
              <div className="flex items-center justify-center w-12 h-12 mb-5 rounded-md bg-indigo-100 text-indigo-600">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">You Are In Control</h3>
              <p className="text-gray-600">
                You have full control over your data. Users can opt out of recognition, and you can request deletion of data at any time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section id="impact" className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold sm:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
              Research-Backed Impact on Your Bottom Line
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              Culture isn't just feel-good. Research shows it drives measurable business outcomes.
            </p>
          </div>

          <div className="grid gap-10 md:grid-cols-3">
            {[
              {
                icon: "fa-chart-line",
                stat: "2.9x",
                title: "Higher Engagement",
                text: "Employees who receive effective recognition are 2.9× more likely to be engaged and experience reduced workplace loneliness.",
                source: "Gallup-Workhuman Research",
                sourceUrl: "https://www.gallup.com/analytics/472658/workplace-recognition-research.aspx"
              },
              {
                icon: "fa-user-friends",
                stat: "45%",
                title: "Lower Turnover",
                text: "Well-recognized employees are 45% less likely to leave their organization within two years, significantly reducing replacement costs.",
                source: "Gallup-Workhuman Research",
                sourceUrl: "https://www.gallup.com/analytics/472658/workplace-recognition-research.aspx"
              },
              {
                icon: "fa-bullseye",
                stat: "23%",
                title: "Higher Profitability",
                text: "Organizations with highly engaged employees see 23% higher profitability compared to those with low engagement.",
                source: "Gallup Research",
                sourceUrl: "https://www.gallup.com/workplace/285674/improve-employee-engagement-workplace.aspx"
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="flex items-center justify-center h-16 w-16 rounded-md bg-indigo-100 text-indigo-600 mb-6 mx-auto">
                  <i className={`fas ${benefit.icon} text-2xl`}></i>
                </div>
                <p className="text-4xl font-bold text-indigo-600 text-center mb-2">{benefit.stat}</p>
                <h3 className="text-xl font-medium text-gray-900 mb-3 text-center">{benefit.title}</h3>
                <p className="text-gray-600 text-center mb-4">{benefit.text}</p>
                <p className="text-xs text-gray-500 text-center">
                  Source: <a href={benefit.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 underline">{benefit.source}</a>
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold sm:text-4xl text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-indigo-600">
              Invest in Your Culture
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
              Start building culture today. Scale as you grow.
            </p>
          </div>

          <div className="mt-8 grid gap-8 lg:grid-cols-2 lg:gap-x-8 max-w-4xl mx-auto">
            <div className="border border-gray-200 rounded-2xl shadow-sm divide-y divide-gray-200 bg-white">
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900">Free</h3>
                <p className="mt-2 text-sm text-gray-500">Perfect for small teams starting their culture journey</p>
                <p className="mt-8">
                  <span className="text-4xl font-extrabold text-gray-900">$0</span>
                  <span className="text-base font-medium text-gray-500">/month</span>
                </p>
                <a
                  href="https://dashboard.apprecio.app"
                  className="mt-8 block w-full bg-indigo-50 border border-indigo-600 rounded-md py-3 text-sm font-semibold text-indigo-600 text-center hover:bg-indigo-100"
                >
                  Get Started
                </a>
              </div>
              <div className="py-6 px-6">
                <h4 className="text-sm font-medium text-gray-900">What's included:</h4>
                <ul className="mt-4 space-y-3">
                  {[
                    "Up to 5 users",
                    "Slack integration",
                    "Send appreciations from Apprecio web portal",
                    "Basic culture metrics",
                    "50 monthly recognitions",
                    "Up to 3 custom company values"
                  ].map((feature, i) => (
                    <li key={i} className="flex">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="ml-3 text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="border-2 border-indigo-600 rounded-2xl shadow-lg divide-y divide-gray-200 bg-white relative">
              <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-0">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-600 text-white">
                  Most Popular
                </span>
              </div>
              <div className="p-6">
                <h3 className="text-lg font-medium text-gray-900">Pro</h3>
                <p className="mt-2 text-sm text-gray-500">Complete culture platform for growing organizations</p>

                {/* Side-by-side pricing */}
                <div className="mt-6 grid grid-cols-2 gap-3">
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-500 mb-1">Monthly</p>
                    <p className="text-3xl font-extrabold text-gray-900">$2.5</p>
                    <p className="text-xs text-gray-500">/user/month</p>
                  </div>
                  <div className="text-center border-l border-gray-200 pl-3">
                    <p className="text-sm font-medium text-gray-500 mb-1">Annual <span className="text-green-600 text-xs">(Save 20%)</span></p>
                    <p className="text-3xl font-extrabold text-gray-900">$2</p>
                    <p className="text-xs text-gray-500">/user/month</p>
                  </div>
                </div>

                <a
                  href="https://dashboard.apprecio.app"
                  className="mt-6 block w-full bg-indigo-600 border border-transparent rounded-md py-3 text-sm font-semibold text-white text-center hover:bg-indigo-700"
                >
                  Get Started
                </a>
              </div>
              <div className="py-6 px-6">
                <h4 className="text-sm font-medium text-gray-900">Everything in Free, plus:</h4>
                <ul className="mt-4 space-y-3">
                  {[
                    "<strong>Unlimited</strong> users",
                    "<strong>Unlimited</strong> monthly recognitions",
                    "<strong>Unlimited</strong> custom company values",
                    "✨ AI-curated email digests of company culture",
                    "Advanced culture analytics",
                    "Departmental leaderboards",
                    "User badges and achievements",
                    "Birthday and work anniversary reminders",
                    "Internal Employee Networking Tool",
                    "Custom appreciation keyword",
                    "Priority support",
                    "Bulk user import/export"
                  ].map((feature, i) => (
                    <li key={i} className="flex">
                      <svg className="flex-shrink-0 h-5 w-5 text-green-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="ml-3 text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: feature }}></span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="get-started" className="bg-gradient-to-r from-indigo-700 to-purple-700 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="pt-10 pb-12 px-6 sm:pt-16 sm:px-16 lg:py-16 lg:px-20">
              <div className="text-center">
                <h3 className="text-3xl font-extrabold text-gray-900">Start Building Your Culture Today</h3>
                <p className="mt-4 text-lg text-gray-600 max-w-3xl mx-auto">
                  Transform your workplace into a culture-driven organization where values matter and people thrive. Start free, scale as you grow.
                </p>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row justify-center gap-4">
                <a
                  href="https://dashboard.apprecio.app"
                  className="w-full sm:w-auto flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  Get Started
                </a>
              </div>
              <p className="mt-4 text-sm text-gray-500 text-center">
                Free for up to 5 users • No credit card required
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-8">
            <div>
              <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }) }} className="text-xl font-bold flex items-center text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
                <i className="fas fa-heart mr-2"></i>
                Apprecio
              </a>
              <p className="mt-2 text-gray-600 text-sm">
                Build a culture of recognition where great work is seen, celebrated, and rewarded.
              </p>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 tracking-wider uppercase mb-4">Product</h3>
              <div className="flex space-x-6">
                <a href="#platform" className="text-sm text-gray-600 hover:text-gray-900">Platform</a>
                <a href="#how-it-works" className="text-sm text-gray-600 hover:text-gray-900">How It Works</a>
                <a href="#privacy" className="text-sm text-gray-600 hover:text-gray-900">Privacy</a>
                <a href="#impact" className="text-sm text-gray-600 hover:text-gray-900">Impact</a>
                <a href="#pricing" className="text-sm text-gray-600 hover:text-gray-900">Pricing</a>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200 flex justify-between items-center">
            <p className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} Apprecio. All rights reserved.
            </p>
          </div>
        </div>

      </footer>

      {/* Modals */}
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        videoUrl="/assets/videos/demo.mp4"
      />
    </div>
  );
};

export default LandingPage;
